import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, createApiClient } from '../App';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import {
  MapPin,
  Star,
  Heart,
  ArrowLeft,
  Plus,
  PencilSimple,
  ShareNetwork,
  ForkKnife,
  SpinnerGap,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import AddVisitModal from '../components/AddVisitModal';
import PlanVisitModal from '../components/PlanVisitModal';
import EditRestaurantModal from '../components/EditRestaurantModal';
import ShareCardModal from '../components/ShareCardModal';
import PhotoLightbox from '../components/PhotoLightbox';
import PhotoGallery from '../components/restaurant/PhotoGallery';
import VisitCard from '../components/restaurant/VisitCard';
import AwardsSection from '../components/restaurant/AwardsSection';
import MilestonesSection from '../components/restaurant/MilestonesSection';
import RestaurantSidebar from '../components/restaurant/RestaurantSidebar';

const RestaurantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const api = createApiClient(token);

  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showAddAward, setShowAddAward] = useState(false);
  const [awardBodies, setAwardBodies] = useState(null);
  const [milestones, setMilestones] = useState([]);

  useEffect(() => {
    fetchRestaurant();
    fetchMilestones();
  }, [id]);

  const fetchRestaurant = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/restaurants/${id}`);
      setRestaurant(response.data);
    } catch (error) {
      console.error('Error fetching restaurant:', error);
      toast.error('Failed to load restaurant');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchMilestones = async () => {
    try {
      const res = await api.get(`/restaurants/${id}/milestones`);
      setMilestones(res.data.milestones || []);
    } catch (error) {
      console.error('Milestones error:', error);
    }
  };

  const handleToggleFavourite = async () => {
    try {
      await api.put(`/restaurants/${id}`, {
        favourite: !restaurant.flags?.favourite
      });
      setRestaurant(prev => ({
        ...prev,
        flags: { ...prev.flags, favourite: !prev.flags?.favourite }
      }));
      toast.success(restaurant.flags?.favourite ? 'Removed from favourites' : 'Added to favourites');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleVisitAdded = async () => {
    try {
      const res = await api.get(`/restaurants/${id}`);
      setRestaurant(res.data);
    } catch {
      // Fallback: just close the modal
    }
    setShowVisitModal(false);
    setEditingVisit(null);
  };

  const handleDeleteVisit = async (visitId) => {
    try {
      await api.delete(`/visits/${visitId}`);
      toast.success('Visit deleted');
      const res = await api.get(`/restaurants/${id}`);
      setRestaurant(res.data);
    } catch {
      toast.error('Failed to delete visit');
    }
  };

  const handlePlanSaved = (updatedRestaurant) => {
    setRestaurant(updatedRestaurant);
    setShowPlanModal(false);
    toast.success('Visit planned!');
  };

  const handleEditSaved = (updatedRestaurant) => {
    setRestaurant(prev => ({ ...prev, ...updatedRestaurant }));
    setShowEditModal(false);
  };

  const handleSetCoverImage = async (photoUrl) => {
    try {
      await api.put(`/restaurants/${id}/cover-image`, { image_url: photoUrl });
      toast.success('Cover image updated!');
      const updated = await api.get(`/restaurants/${id}`);
      setRestaurant(updated.data);
    } catch {
      toast.error('Failed to update cover image');
    }
  };

  const handleCategorizePhoto = async (attId, newType) => {
    try {
      await api.patch(`/attachments/${attId}/category`, { attachment_type: newType });
      toast.success('Photo categorised!');
      const updated = await api.get(`/restaurants/${id}`);
      setRestaurant(updated.data);
    } catch {
      toast.error('Failed to update category');
    }
  };

  const handlePhotoUpload = async (files, category) => {
    const visits = restaurant?.visits || [];
    const latestVisit = visits.length > 0 ? visits[0] : null;
    if (!latestVisit) {
      toast.error('Add a visit first before uploading photos');
      return;
    }
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('attachment_type', category);
        await api.post(`/visits/${latestVisit.id}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} added to ${category}`);
      fetchRestaurant();
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const handleAddToCalendar = async () => {
    try {
      await api.post(`/restaurants/${id}/google-calendar`);
      toast.success('Added to Google Calendar!');
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('not connected')) {
        toast.error('Please connect Google Calendar first');
      } else {
        toast.error('Failed to add to calendar');
      }
    }
  };

  const handleGoogleEnrich = async () => {
    setEnriching(true);
    try {
      const res = await api.post(`/restaurants/${id}/google-enrich`);
      const fields = res.data.enriched_fields || [];
      toast.success(
        fields.length > 0
          ? `Enriched ${fields.length} field${fields.length !== 1 ? 's' : ''} from Google`
          : 'No new data found — restaurant is already up to date'
      );
      const updated = await api.get(`/restaurants/${id}`);
      setRestaurant(updated.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to enrich from Google';
      toast.error(msg);
    } finally {
      setEnriching(false);
    }
  };

  const handleAddAward = async (awardBody, level, year) => {
    try {
      const response = await api.post(`/restaurants/${id}/awards`, {
        award_body: awardBody,
        level,
        year
      });
      setRestaurant(prev => ({
        ...prev,
        awards: [...(prev.awards || []), response.data]
      }));
      setShowAddAward(false);
      toast.success('Award added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add award');
    }
  };

  const handleRemoveAward = async (awardId) => {
    try {
      await api.delete(`/restaurants/${id}/awards/${awardId}`);
      setRestaurant(prev => ({
        ...prev,
        awards: (prev.awards || []).filter(a => a.id !== awardId)
      }));
      toast.success('Award removed');
    } catch (error) {
      toast.error('Failed to remove award');
    }
  };

  const loadAwardBodies = async () => {
    if (!awardBodies) {
      try {
        const res = await api.get('/restaurants/award-bodies');
        setAwardBodies(res.data.award_bodies);
      } catch (e) {
        console.error('Failed to load award bodies:', e);
      }
    }
    setShowAddAward(true);
  };

  // Build flat allPhotos for lightbox (matches PhotoGallery render order)
  const allPhotos = buildAllPhotos(restaurant);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F5] flex items-center justify-center">
        <SpinnerGap className="w-12 h-12 animate-spin text-[#A94438]" />
      </div>
    );
  }

  if (!restaurant) return null;

  const defaultImage = 'https://images.pexels.com/photos/1327393/pexels-photo-1327393.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

  const getStateLabel = (state) => {
    switch (state) {
      case 'want_to_try': return 'Want to Try';
      case 'planned': return 'Planned';
      case 'visited': return 'Visited';
      default: return state;
    }
  };

  const getStateClass = (state) => {
    switch (state) {
      case 'want_to_try': return 'status-want-to-try';
      case 'planned': return 'status-planned';
      case 'visited': return 'status-visited';
      default: return '';
    }
  };

  return (
    <div className="pb-8" data-testid="restaurant-detail">
      <SEO
        title={restaurant.name}
        description={`${restaurant.name}${restaurant.suburb ? ` in ${restaurant.suburb}` : ''} — ${restaurant.cuisine?.join(', ') || 'Cape Town restaurant'}. ${restaurant.description?.slice(0, 120) || 'View photos, ratings, visits and more.'}`}
        path={`/restaurants/${id}`}
        noindex
      />
      {/* Hero */}
      <div className="relative h-[300px] sm:h-[400px]">
        <img
          src={restaurant.hero_image_url || defaultImage}
          alt={restaurant.name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        <div className="hero-overlay" />

        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft weight="bold" className="w-5 h-5" />
        </button>

        <div className="absolute top-4 right-4 flex gap-2">
          <button
            onClick={() => setShowEditModal(true)}
            className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
            data-testid="edit-restaurant-btn"
            title="Edit restaurant"
          >
            <PencilSimple weight="bold" className="w-5 h-5 text-[#1A1918]" />
          </button>
          <button
            onClick={() => setShowShareModal(true)}
            className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
            data-testid="share-restaurant-btn"
            title="Share card"
          >
            <ShareNetwork weight="bold" className="w-5 h-5 text-[#1A1918]" />
          </button>
          <button
            onClick={handleToggleFavourite}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              restaurant.flags?.favourite
                ? 'bg-[#A94438] text-white'
                : 'bg-white/90 backdrop-blur-sm text-[#1A1918] hover:bg-white'
            }`}
            data-testid="favourite-button"
          >
            <Heart weight={restaurant.flags?.favourite ? 'fill' : 'regular'} className="w-5 h-5" />
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <Badge className={`${getStateClass(restaurant.state)} border-0 mb-3`}>
            {getStateLabel(restaurant.state)}
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-2">
            {restaurant.name}
          </h1>
          {restaurant.suburb && (
            <div className="flex items-center gap-2 text-white/80">
              <MapPin weight="fill" className="w-5 h-5" />
              <span>{restaurant.suburb}, Cape Town</span>
            </div>
          )}
        </div>
      </div>

      <div className="container-app py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="card-elevated p-4 text-center">
                <div className="flex items-center justify-center gap-1 text-[#A94438] mb-1">
                  <Star weight="fill" className="w-5 h-5" />
                  <span className="text-2xl font-bold">{restaurant.aggregate_rating || '-'}</span>
                </div>
                <p className="text-xs text-[#7A7873]">Rating</p>
              </div>
              <div className="card-elevated p-4 text-center">
                <div className="text-2xl font-bold text-[#1A1918] mb-1">{restaurant.visit_count || 0}</div>
                <p className="text-xs text-[#7A7873]">Visits</p>
              </div>
              <div className="card-elevated p-4 text-center">
                <div className="text-2xl font-bold text-[#4B5945] mb-1">
                  {restaurant.price_range ? 'R'.repeat(
                    restaurant.price_range === 'budget' ? 1 :
                    restaurant.price_range === 'mid' ? 2 :
                    restaurant.price_range === 'premium' ? 3 : 4
                  ) : '-'}
                </div>
                <p className="text-xs text-[#7A7873]">Price</p>
              </div>
            </div>

            {/* Description */}
            {restaurant.description && (
              <div className="card-elevated card-padding">
                <h2 className="font-heading text-xl font-semibold mb-3">About</h2>
                <p className="text-[#7A7873] leading-relaxed">{restaurant.description}</p>
              </div>
            )}

            {/* Cuisine tags */}
            {restaurant.cuisine && restaurant.cuisine.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {restaurant.cuisine.map((c, i) => (
                  <Badge key={i} variant="secondary" className="badge-cuisine">{c}</Badge>
                ))}
              </div>
            )}

            {/* Awards */}
            <AwardsSection
              awards={restaurant.awards}
              awardBodies={awardBodies}
              showAddAward={showAddAward}
              onLoadAwardBodies={loadAwardBodies}
              onAddAward={handleAddAward}
              onRemoveAward={handleRemoveAward}
              onCancelAdd={() => setShowAddAward(false)}
            />

            {/* Milestones */}
            <MilestonesSection milestones={milestones} />

            {/* Photo Gallery */}
            <PhotoGallery
              restaurant={restaurant}
              onSetCover={handleSetCoverImage}
              onCategorize={handleCategorizePhoto}
              onUploadPhotos={handlePhotoUpload}
              onOpenLightbox={(idx) => { setLightboxIndex(idx); setLightboxOpen(true); }}
            />

            {/* Visit history */}
            <div className="card-elevated card-padding">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-semibold">Visit History</h2>
                <Button
                  onClick={() => { setEditingVisit(null); setShowVisitModal(true); }}
                  className="btn-primary"
                  data-testid="add-visit-btn"
                >
                  <Plus weight="bold" className="w-4 h-4 mr-2" />
                  Log Visit
                </Button>
              </div>

              {(!restaurant.visits || restaurant.visits.length === 0) ? (
                <div className="text-center py-8">
                  <ForkKnife weight="duotone" className="w-12 h-12 text-[#E8DCD0] mx-auto mb-3" />
                  <p className="text-[#7A7873]">No visits yet</p>
                  <p className="text-sm text-[#7A7873]">Log your first visit to start tracking</p>
                </div>
              ) : (
                <div className="visit-timeline">
                  {restaurant.visits.map((visit) => (
                    <VisitCard
                      key={visit.id}
                      visit={visit}
                      onEdit={(v) => { setEditingVisit(v); setShowVisitModal(true); }}
                      onDelete={handleDeleteVisit}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <RestaurantSidebar
            restaurant={restaurant}
            enriching={enriching}
            onShowPlanModal={() => setShowPlanModal(true)}
            onShowVisitModal={() => { setEditingVisit(null); setShowVisitModal(true); }}
            onAddToCalendar={handleAddToCalendar}
            onGoogleEnrich={handleGoogleEnrich}
          />
        </div>
      </div>

      {/* Modals */}
      <AddVisitModal
        open={showVisitModal}
        onOpenChange={(v) => { setShowVisitModal(v); if (!v) setEditingVisit(null); }}
        restaurantId={id}
        onSuccess={handleVisitAdded}
        editVisit={editingVisit}
      />
      <PlanVisitModal
        open={showPlanModal}
        onOpenChange={setShowPlanModal}
        restaurantId={id}
        restaurant={restaurant}
        onSuccess={handlePlanSaved}
      />
      <EditRestaurantModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        restaurant={restaurant}
        onSuccess={handleEditSaved}
      />
      <ShareCardModal
        open={showShareModal}
        onOpenChange={setShowShareModal}
        restaurant={restaurant}
      />
      <PhotoLightbox
        images={allPhotos}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

// Helper to build flat photo list for lightbox (matches PhotoGallery render order)
const PHOTO_CATEGORIES_ORDER = ['food', 'drinks', 'menu', 'interior', 'exterior', 'receipt', 'venue_photo'];

function buildAllPhotos(restaurant) {
  if (!restaurant) return [];
  const photosByCategory = {};

  (restaurant.visits || []).forEach(visit => {
    const visitDateStr = visit.visited_at
      ? new Date(visit.visited_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
      : 'Unknown date';

    (visit.attachments || []).forEach(att => {
      const cat = att.label || att.attachment_type || 'venue_photo';
      const normCat = ['food', 'drinks', 'menu', 'interior', 'exterior', 'receipt'].includes(cat) ? cat : 'venue_photo';
      const photoObj = {
        id: att.id,
        url: att.url
          ? `${process.env.REACT_APP_BACKEND_URL}${att.url.startsWith('/api') ? '' : '/api'}${att.url.startsWith('/api') ? att.url : att.url}`
          : att.file_path
            ? `${process.env.REACT_APP_BACKEND_URL}/api${att.file_path}`
            : null,
        caption: `${visit.meal_type || 'Visit'} - ${visitDateStr}`,
        type: normCat,
      };
      if (!photoObj.url) return;
      if (!photosByCategory[normCat]) photosByCategory[normCat] = [];
      photosByCategory[normCat].push(photoObj);
    });
  });

  return PHOTO_CATEGORIES_ORDER.filter(k => (photosByCategory[k] || []).length > 0)
    .flatMap(k => photosByCategory[k]);
}

export default RestaurantDetail;
